import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { CardSpec, ItemSpec } from '../../../engine';
import {
  SUPERSTITION_INFO,
  isBolted,
  isRotten,
  onLoan,
  rots,
  type LotOffer,
  type NightRun,
  type Offer,
  type Rarity,
} from '../../../campaign';
import { t, type MessageKey } from '../../../i18n';
import { faceLong, itemName, itemText } from '../../../app/describe';
import { CardView } from '../CardView';
import { Modal } from '../Modal';
import { glossaryFor } from './glossary';
import { NightIcon, omenIcon, type NightIconName } from './NightIcon';
import { NightObject, type ObjectSpec } from './Objects';
import { BARGAIN_PAWNS, type BargainId } from '../../../campaign';

/**
 * Everything the Long Night sells or gives, shown one way: a card with an icon, a name and a
 * price, that opens a sheet saying exactly what it does and explaining its words.
 */

export interface ShowItem {
  readonly kindLabel: string;
  readonly name: string;
  readonly text: string;
  readonly icon: NightIconName;
  readonly rarity?: Rarity;
  /** Short states: a Finish, On Loan, Rotting… */
  readonly badges?: readonly string[];
  /** A deck card is drawn as a card. */
  readonly card?: CardSpec;
  /** Anything else is drawn as the object it is (night/Objects.tsx). */
  readonly object?: ObjectSpec;
}

export const cardName = (c: CardSpec): string =>
  c.kind === 'wild' ? t('card.wild') : faceLong(c.face);

function cardText(c: CardSpec): string {
  const parts: string[] = [];
  if (c.trick)
    parts.push(
      `${itemName(`trick.${c.trick}`)}: ${itemText(`trick.${c.trick}`)}`,
      itemText('trick'),
    );
  else parts.push(t(c.kind === 'wild' ? 'night.card.wild' : 'night.card.plain'));
  if (c.stamp) parts.push(`${itemName(c.stamp)}: ${itemText(c.stamp)}`);
  if (c.finish) parts.push(`${itemName(c.finish)}: ${itemText(c.finish)}`);
  if (c.iou) parts.push(t('night.card.iou'));
  return parts.join('\n\n');
}

export function showOffer(o: Offer): ShowItem {
  switch (o.kind) {
    case 'superstition': {
      const rarity = SUPERSTITION_INFO[o.id as keyof typeof SUPERSTITION_INFO]?.rarity;
      const badges = [o.finish ? itemName(o.finish) : '', o.loan ? t('night.onLoan') : ''];
      const extra = [
        o.finish ? `${itemName(o.finish)}: ${itemText(o.finish)}` : '',
        o.loan ? t('night.loan') : '',
      ];
      return {
        kindLabel: t('night.kind.superstition'),
        name: itemName(o.id),
        text: [itemText(o.id), ...extra].filter(Boolean).join('\n\n'),
        icon: 'superstition',
        ...(rarity ? { rarity } : {}),
        badges: badges.filter(Boolean),
        object: {
          kind: 'superstition',
          emblem: o.id,
          ...(rarity ? { rarity } : {}),
          ...(o.finish ? { finish: o.finish } : {}),
          ...(o.loan ? { loan: true } : {}),
        },
      };
    }
    case 'curio':
      return {
        kindLabel: t('night.kind.curio'),
        name: itemName(o.id),
        text: `${itemText(o.id)}\n\n${t(BENCH.has(o.id) ? 'night.curio.bench' : 'night.curio.table')}`,
        icon: 'curio',
        object: { kind: 'curio', emblem: o.id },
      };
    case 'appraisal':
      return {
        kindLabel: t('night.kind.appraisal'),
        name: itemName(`appraisal.${o.id}`),
        text: `${itemText(`appraisal.${o.id}`)}\n\n${t('night.appraisal.how')}`,
        icon: 'appraisal',
        object: { kind: 'appraisal', emblem: `appraisal.${o.id}` },
      };
    case 'card':
      return {
        kindLabel: o.card.trick ? t('night.kind.trick') : t('night.kind.card'),
        name: o.card.trick ? itemName(`trick.${o.card.trick}`) : cardName(o.card),
        text: cardText(o.card),
        icon: o.card.trick ? 'trick' : 'card',
        card: o.card,
        badges: [
          o.card.stamp ? itemName(o.card.stamp) : '',
          o.card.finish ? itemName(o.card.finish) : '',
        ].filter(Boolean),
      };
    case 'carving':
      return {
        kindLabel: t('night.kind.carving'),
        name: itemName(o.id),
        text: `${itemText(o.id)}\n\n${t('night.carving.how')}`,
        icon: 'carving',
        object: { kind: 'carving', emblem: `carving.${o.id}` },
      };
    case 'bargain':
      return {
        kindLabel: t('night.kind.bargain'),
        name: itemName(`bargain.${o.id}`),
        text: itemText(`bargain.${o.id}`),
        icon: 'bargain',
        object: bargainObject(o.id),
      };
    case 'route':
      return {
        kindLabel: t('night.kind.route'),
        name: t('night.offer.route', { name: o.id }),
        text: t('night.offer.routeText'),
        icon: 'route',
      };
    case 'omen':
      return {
        kindLabel: t('night.kind.omen'),
        name: itemName(`omen.${o.id}`),
        text: `${itemText(`omen.${o.id}`)}\n\n${t('night.offer.omenPlace')}`,
        icon: omenIcon(o.id),
        object: { kind: 'emblem', emblem: `omen.${o.id}` },
      };
  }
}

/** A Superstition you own, with its state this run. */
export function showMantelItem(run: NightRun, item: ItemSpec): ShowItem {
  const base = showOffer({
    kind: 'superstition',
    id: item.id,
    ...(item.finish ? { finish: item.finish } : {}),
  });
  const badges = [...(base.badges ?? [])];
  const notes: string[] = [];
  if (onLoan(run, item.uid)) {
    badges.push(t('night.onLoan'));
    notes.push(t('night.loan'));
  }
  if (isBolted(run, item.uid)) {
    badges.push(t('night.bolted'));
    notes.push(t('night.boltedText'));
  }
  if (rots(run, item.uid)) {
    const age = run.counters[`rot:${item.uid}`] ?? 0;
    badges.push(
      isRotten(run, item.uid) ? t('night.rotten') : t('night.rotting', { n: Math.max(0, 5 - age) }),
    );
    notes.push(t('night.rottingText'));
  }
  if (item.value !== undefined) notes.push(t('ledger.value', { n: item.value }));
  const age = run.counters[`rot:${item.uid}`] ?? 0;
  return {
    ...base,
    text: [base.text, ...notes].join('\n\n'),
    badges,
    object: mantelObject(item, {
      loan: onLoan(run, item.uid),
      bolted: isBolted(run, item.uid),
      rot: rots(run, item.uid) ? (isRotten(run, item.uid) ? 1 : Math.min(1, (age + 1) / 6)) : 0,
    }),
  };
}

/** A Superstition as its framed curio, with its stickers and its growing value (§12.1). */
export function mantelObject(
  item: ItemSpec,
  state: { loan?: boolean; bolted?: boolean; rot?: number } = {},
): ObjectSpec {
  const rarity = SUPERSTITION_INFO[item.id as keyof typeof SUPERSTITION_INFO]?.rarity;
  return {
    kind: 'superstition',
    emblem: item.id,
    ...(rarity ? { rarity } : {}),
    ...(item.finish ? { finish: item.finish } : {}),
    ...(state.loan ? { loan: true } : {}),
    ...(state.bolted ? { bolted: true } : {}),
    ...(state.rot ? { rot: state.rot } : {}),
    // Piggy Bank grows in brass; the rest grow a multiplier.
    ...(item.value !== undefined
      ? { plate: item.id === 'piggy-bank' ? `¤${item.value}` : `× ${item.value}` }
      : {}),
  };
}

export const showCurio = (item: ItemSpec): ShowItem => showOffer({ kind: 'curio', id: item.id });

export const showFixture = (id: string): ShowItem => ({
  kindLabel: t('night.kind.fixture'),
  name: itemName(`fixture.${id}`),
  text: `${itemText(`fixture.${id}`)}\n\n${t('night.fixture.how')}`,
  icon: 'fixture',
  object: { kind: 'fixture', emblem: `fixture.${id}` },
});

export const showLot = (lot: LotOffer, show: number, take: number): ShowItem => ({
  kindLabel: t(`night.lotSize.${lot.size}`),
  name: itemName(`lot.${lot.kind}`),
  text: `${itemText(`lot.${lot.kind}`)}\n\n${t('night.lot.how', { show, take })}`,
  icon: 'lot',
  object: { kind: 'lot', lot: lot.kind, size: lot.size },
});

/** A Bargain's card: its cost in your pawns, or what else it takes, in his ink (§12.7). */
export function bargainObject(id: string): ObjectSpec {
  const pawns = BARGAIN_PAWNS[id as BargainId] ?? 0;
  return {
    kind: 'bargain',
    emblem: `bargain.${id}`,
    pawns,
    ...(pawns === 0 ? { cost: t(`night.bargainCost.${id}` as MessageKey) } : {}),
  };
}

const BENCH = new Set([
  'whittling-knife',
  'ink-pot',
  'file',
  'rubber-stamp',
  'tracing-paper',
  'coin-purse',
  'appraisers-note',
]);
export const isBenchCurio = (id: string): boolean => BENCH.has(id);

/** How wide an object is drawn in a card of the grid and in its sheet. */
export function objectSize(o: ObjectSpec, where: 'card' | 'sheet'): number {
  const big = where === 'sheet';
  switch (o.kind) {
    case 'superstition':
    case 'bargain':
      return big ? 76 : 52;
    case 'lot':
    case 'appraisal':
    case 'favor':
      return big ? 92 : 70;
    case 'fixture':
      return big ? 84 : 62;
    default:
      return big ? 76 : 56;
  }
}

/** One thing as a card: icon (or the card itself), name, price. */
export function ItemCard({
  item,
  price,
  sold = false,
  short = false,
  selected = false,
  onClick,
}: {
  item: ShowItem;
  price?: number;
  sold?: boolean;
  /** The price is more than the brass there is. */
  short?: boolean;
  selected?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span
        className={`ln-card-art ${item.card || item.object ? 'has-card' : ''} ${item.rarity ? `rarity-${item.rarity}` : ''}`}
      >
        {item.card ? (
          <CardView
            size="sm"
            face={item.card.kind === 'normal' ? item.card.face : null}
            wild={item.card.kind === 'wild'}
            stamp={item.card.stamp}
            finish={item.card.finish}
            trick={item.card.trick}
            label=""
          />
        ) : item.object ? (
          <NightObject spec={item.object} size={objectSize(item.object, 'card')} />
        ) : (
          <NightIcon name={item.icon} size={30} />
        )}
      </span>
      <span className="ln-card-kind">{item.kindLabel}</span>
      <span className="ln-card-name">{item.name}</span>
      {item.badges && item.badges.length > 0 && (
        <span className="ln-card-badges">{item.badges.join(' · ')}</span>
      )}
      {price !== undefined && !sold && (
        <span className={`ln-price ${short ? 'is-short' : ''}`}>
          {price === 0 ? t('night.free') : `¤${price}`}
        </span>
      )}
      {sold && <span className="ln-sold">{t('night.sold')}</span>}
    </>
  );
  const cls = `ln-card ${sold ? 'is-sold' : ''} ${selected ? 'is-selected' : ''} ${item.rarity ? `rarity-${item.rarity}` : ''}`;
  return onClick && !sold ? (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      aria-label={`${item.name}. ${item.kindLabel}`}
    >
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/**
 * Stepping through a group from one thing's sheet (a Mantel, a side's kit): arrows beside the
 * art, a swipe across it, or the arrow keys. It wraps round at the ends.
 */
export interface SheetBrowse {
  readonly at: number;
  readonly of: number;
  readonly onStep: (dir: -1 | 1) => void;
}

/** Which of a group is open in its sheet, and the browsing that goes with it. */
export function useBrowser<T>(list: readonly T[]): {
  readonly open: T | null;
  readonly show: (index: number) => void;
  readonly close: () => void;
  readonly browse: SheetBrowse | undefined;
} {
  const [at, setAt] = useState<number | null>(null);
  const i = at !== null && list.length > 0 ? Math.min(at, list.length - 1) : null;
  return {
    open: i !== null ? (list[i] ?? null) : null,
    show: setAt,
    close: () => setAt(null),
    browse:
      i !== null && list.length > 1
        ? {
            at: i,
            of: list.length,
            onStep: (d) => setAt((i + d + list.length) % list.length),
          }
        : undefined,
  };
}

/** A pixel chevron for the sheet's arrows, pointing right (mirrored for left). */
function Chevron() {
  return (
    <svg viewBox="0 0 6 10" width="12" height="20" shapeRendering="crispEdges" aria-hidden="true">
      <path
        fill="currentColor"
        d="M0 0h2v1h1v1h1v1h1v1h1v2h-1v1h-1v1h-1v1h-1v1h-2v-1h1v-1h1v-1h1v-1h1v-2h-1v-1h-1v-1h-1v-1h-1z"
      />
    </svg>
  );
}

export interface SheetAction {
  readonly label: string;
  readonly onClick: () => void;
  readonly primary?: boolean;
  readonly danger?: boolean;
  readonly disabled?: boolean;
}

/** What one thing does, in full, with its words explained and what can be done with it. */
export function ItemSheet({
  item,
  price,
  note,
  actions,
  children,
  browse,
  onClose,
}: {
  item: ShowItem;
  price?: number;
  /** Why an action is not possible (not enough brass, the Mantel is full…). */
  note?: string | null;
  actions: readonly SheetAction[];
  children?: ReactNode;
  /** Opened from a group: arrows to the one before and the one after. */
  browse?: SheetBrowse;
  onClose: () => void;
}) {
  const terms = glossaryFor(item.text);
  // The way the last step went, so the next one slides in from that side.
  const [dir, setDir] = useState<-1 | 1 | 0>(0);
  const step = (d: -1 | 1) => {
    if (!browse) return;
    setDir(d);
    browse.onStep(d);
  };
  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  });
  useEffect(() => {
    if (!browse) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') stepRef.current(-1);
      else if (e.key === 'ArrowRight') stepRef.current(1);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [browse]);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  return (
    <Modal
      title={item.name}
      onClose={onClose}
      className="ln-sheet"
      footer={
        actions.length > 0 ? (
          <>
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                className={`btn ${a.primary ? 'btn-primary' : ''} ${a.danger ? 'btn-danger' : ''}`}
                disabled={a.disabled}
                data-autofocus={a.primary ? true : undefined}
                onClick={a.onClick}
              >
                {a.label}
              </button>
            ))}
          </>
        ) : undefined
      }
    >
      <div
        className={`ln-sheet-head ${browse ? 'is-browsing' : ''}`}
        onPointerDown={(e) => {
          if (browse) swipe.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={(e) => {
          const s = swipe.current;
          swipe.current = null;
          if (!s) return;
          const dx = e.clientX - s.x;
          // A sideways swipe across the head steps, as on a card table.
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(e.clientY - s.y) * 1.5)
            step(dx < 0 ? 1 : -1);
        }}
        onPointerCancel={() => (swipe.current = null)}
      >
        {browse && (
          <button
            type="button"
            className="ln-step is-prev"
            aria-label={t('item.prev')}
            onClick={() => step(-1)}
          >
            <Chevron />
          </button>
        )}
        <span
          key={browse ? `${item.kindLabel}:${item.name}:${browse.at}` : undefined}
          className={`ln-card-art is-large ${item.card || item.object ? 'has-card' : ''} ${item.rarity ? `rarity-${item.rarity}` : ''} ${
            browse && dir !== 0 ? (dir > 0 ? 'is-from-right' : 'is-from-left') : ''
          }`}
        >
          {item.card ? (
            <CardView
              size="md"
              face={item.card.kind === 'normal' ? item.card.face : null}
              wild={item.card.kind === 'wild'}
              stamp={item.card.stamp}
              finish={item.card.finish}
              trick={item.card.trick}
              label=""
            />
          ) : item.object ? (
            <NightObject spec={item.object} size={objectSize(item.object, 'sheet')} />
          ) : (
            <NightIcon name={item.icon} size={40} />
          )}
        </span>
        <div className="ln-sheet-id">
          {browse && (
            <p className="ln-step-count">{t('item.count', { n: browse.at + 1, of: browse.of })}</p>
          )}
          <p className="ln-eyebrow">
            {item.kindLabel}
            {item.rarity ? ` · ${t(`night.rarity.${item.rarity}`)}` : ''}
          </p>
          {item.badges && item.badges.length > 0 && (
            <p className="ln-sheet-badges">
              {item.badges.map((b) => (
                <span key={b} className="ln-badge">
                  {b}
                </span>
              ))}
            </p>
          )}
          {price !== undefined && (
            <p className="ln-sheet-price">{price === 0 ? t('night.free') : `¤${price}`}</p>
          )}
        </div>
        {browse && (
          <button
            type="button"
            className="ln-step is-next"
            aria-label={t('item.next')}
            onClick={() => step(1)}
          >
            <Chevron />
          </button>
        )}
      </div>
      {item.text.split('\n\n').map((para, i) => (
        <p key={i} className={i === 0 ? 'ln-sheet-text' : 'ln-sheet-more'}>
          {para}
        </p>
      ))}
      {children}
      {note && <p className="ln-sheet-note">{note}</p>}
      {terms.length > 0 && (
        <dl className="ln-terms">
          {terms.map((x) => (
            <div key={x.term}>
              <dt>{x.term}</dt>
              <dd>{x.text}</dd>
            </div>
          ))}
        </dl>
      )}
    </Modal>
  );
}

/** Brass, as a pill: gold, red in debt. */
export function BrassPill({ brass, large = false }: { brass: number; large?: boolean }) {
  return (
    <span
      className={`ln-brass ${brass < 0 ? 'is-debt' : ''} ${large ? 'is-large' : ''}`}
      title={t('ledger.brass')}
    >
      <NightIcon name="brass" size={large ? 18 : 14} />
      {brass}
    </span>
  );
}
